import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  StatusBadge,
  Textarea,
} from '../components/ui';
import { api, type Campaign } from '../lib/api';
import { date } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

export function Campaigns() {
  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');
  const [showNew, setShowNew] = useState(false);

  const items = campaigns.data?.campaigns ?? [];

  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Each campaign holds its own personas, cadence, and prompts."
        helpHref="campaigns"
        actions={
          <Button variant="primary" onClick={() => setShowNew(true)}>
            New campaign
          </Button>
        }
      />

      {campaigns.loading && <Loading />}
      {campaigns.error && <ErrorNote message={campaigns.error} onRetry={campaigns.reload} />}

      {!campaigns.loading && items.length === 0 && (
        <EmptyState
          title="No campaigns yet"
          description="A campaign groups prospects who should hear the same story — a service line, a vertical, a quarter."
          action={<Button variant="primary" onClick={() => setShowNew(true)}>Create your first campaign</Button>}
        />
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((campaign) => (
          <Link key={campaign.id} to={`/campaigns/${campaign.id}`} className="group block">
            <Card className="h-full transition-shadow group-hover:shadow-md">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-ink-900 group-hover:text-accent-700">{campaign.name}</h3>
                <StatusBadge status={campaign.status} />
              </div>
              {campaign.description && (
                <p className="mt-1 line-clamp-2 text-sm text-ink-600">{campaign.description}</p>
              )}

              <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-ink-100 pt-3 text-center">
                <div>
                  <dt className="text-xs text-ink-500">Prospects</dt>
                  <dd className="text-lg font-semibold tabular-nums text-ink-900">
                    {campaign.prospect_count ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500">Replied</dt>
                  <dd className="text-lg font-semibold tabular-nums text-violet-600">
                    {campaign.replied_count ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500">Won</dt>
                  <dd className="text-lg font-semibold tabular-nums text-emerald-600">
                    {campaign.won_count ?? 0}
                  </dd>
                </div>
              </dl>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Badge>{campaign.new_count ?? 0} new</Badge>
                <Badge tone="blue">{campaign.contacted_count ?? 0} contacted</Badge>
                <Badge tone="red">{campaign.lost_count ?? 0} lost</Badge>
                <span className="ml-auto text-xs text-ink-400">{date(campaign.created_at)}</span>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <NewCampaignModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onDone={() => {
          setShowNew(false);
          campaigns.reload();
        }}
      />
    </>
  );
}

function NewCampaignModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState('');

  const create = useAction(async () => {
    await api.post('/api/campaigns', { name, description, context });
    setName('');
    setDescription('');
    setContext('');
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="New campaign">
      <div className="space-y-3">
        <Field label="Name">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="GSA Schedule Q3 2026"
          />
        </Field>
        <Field label="Description" hint="For you, not for Claude.">
          <Textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
        </Field>
        <Field
          label="Campaign context"
          hint="Goes into every Claude prompt for this campaign — the offer, the proof, the constraints."
        >
          <Textarea
            rows={5}
            value={context}
            onChange={(event) => setContext(event.target.value)}
            placeholder="Optime Services holds a GSA Schedule for multilingual translation. Buyers care about FAR compliance, vendor consolidation, and turnaround on classified-adjacent material."
          />
        </Field>

        {create.error && <ErrorNote message={create.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.pending} disabled={!name.trim()} onClick={() => void create.run()}>
            Create campaign
          </Button>
        </div>
      </div>
    </Modal>
  );
}
